import { firebaseApp } from '../firebase';
import {
  buildAnalyticsEvent,
} from './analyticsContract.js';
import { buildAnalyticsSubjectId } from './analyticsSubjectIdentity.js';

let analyticsProviderPromise;
let analyticsIdentityRevision = 0;

function analyticsCollectionEnabled() {
  return process.env.REACT_APP_FIREBASE_ANALYTICS_ENABLED === 'true';
}

async function resolveFirebaseAnalyticsProvider({
  app = firebaseApp,
  collectionEnabled = analyticsCollectionEnabled(),
  loadAnalyticsModule = () => import('firebase/analytics'),
} = {}) {
  if (!collectionEnabled) return null;
  try {
    const analyticsModule = await loadAnalyticsModule();
    if (!await analyticsModule.isSupported()) return null;
    return {
      instance: analyticsModule.getAnalytics(app),
      logEvent: analyticsModule.logEvent,
      setUserId: analyticsModule.setUserId,
    };
  } catch {
    return null;
  }
}

async function getFirebaseAnalyticsProvider() {
  if (!analyticsCollectionEnabled()) return null;
  if (!analyticsProviderPromise) {
    analyticsProviderPromise = resolveFirebaseAnalyticsProvider();
  }
  return analyticsProviderPromise;
}

async function getFirebaseAnalytics() {
  const provider = await getFirebaseAnalyticsProvider();
  return provider?.instance || null;
}

export async function logCanonicalAnalyticsEvent(eventName, params = {}) {
  const event = buildAnalyticsEvent(eventName, {
    platform: 'web',
    app_version: process.env.REACT_APP_VERSION || '0.0.0',
    ...params,
  });
  const provider = await getFirebaseAnalyticsProvider();
  if (!provider) {
    return {
      recorded: false,
      reason: 'analytics_disabled',
      event,
    };
  }
  provider.logEvent(provider.instance, event.name, event.params);
  return {
    recorded: true,
    event,
  };
}

export async function syncCanonicalAnalyticsUser(
  firebaseUid,
  {
    buildSubjectId = buildAnalyticsSubjectId,
    getAnalyticsInstance = getFirebaseAnalytics,
    setUserId,
  } = {},
) {
  const revision = ++analyticsIdentityRevision;
  const analytics = await getAnalyticsInstance();
  if (!analytics) {
    return {
      synced: false,
      reason: 'analytics_disabled',
      subjectId: null,
    };
  }

  const subjectId = firebaseUid
    ? await buildSubjectId(firebaseUid).catch(() => null)
    : null;
  if (revision !== analyticsIdentityRevision) {
    return {
      synced: false,
      reason: 'superseded',
      subjectId: null,
    };
  }

  const setAnalyticsUserId = setUserId
    || (await getFirebaseAnalyticsProvider())?.setUserId;
  if (typeof setAnalyticsUserId !== 'function') {
    return {
      synced: false,
      reason: 'analytics_disabled',
      subjectId: null,
    };
  }
  setAnalyticsUserId(analytics, subjectId);
  return {
    synced: true,
    reason: subjectId || !firebaseUid ? 'updated' : 'invalid_uid',
    subjectId,
  };
}

export {
  analyticsCollectionEnabled,
  getFirebaseAnalytics,
  resolveFirebaseAnalyticsProvider,
};
